"use client";

import { Check, Copy, X } from "@phosphor-icons/react";
import Anser from "anser";
import type { ComponentProps, HTMLAttributes } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

// In-house ANSI renderer. Replaces the unmaintained `ansi-to-react`
// (CJS-only, broken under Vite 8's ESM interop — React #130). Uses
// `anser` directly for parsing, then maps the bundle array to styled
// <span>s. Linkify is intentionally dropped — terminal output rarely
// benefits from auto-link, and dropping the dep avoids another piece
// of CJS-only baggage.
const Ansi = ({ children: text }: { children: string }) => {
  const bundles = Anser.ansiToJson(text, {
    json: true,
    remove_empty: true,
    use_classes: false,
  });
  return (
    <code>
      {bundles.map((bundle, i) => {
        const style: React.CSSProperties = {};
        if (bundle.fg) style.color = `rgb(${bundle.fg})`;
        if (bundle.bg) style.backgroundColor = `rgb(${bundle.bg})`;
        if (bundle.decoration === "bold") style.fontWeight = "bold";
        else if (bundle.decoration === "dim") style.opacity = 0.5;
        else if (bundle.decoration === "italic") style.fontStyle = "italic";
        else if (bundle.decoration === "underline")
          style.textDecoration = "underline";
        else if (bundle.decoration === "strikethrough")
          style.textDecoration = "line-through";
        else if (bundle.decoration === "hidden") style.visibility = "hidden";
        // biome-ignore lint/suspicious/noArrayIndexKey: bundle order is stable across renders for a given input
        return (
          <span key={i} style={style}>
            {bundle.content}
          </span>
        );
      })}
    </code>
  );
};

interface TerminalContextType {
  output: string;
  isStreaming: boolean;
  autoScroll: boolean;
  onClear?: () => void;
}

const TerminalContext = createContext<TerminalContextType>({
  autoScroll: true,
  isStreaming: false,
  output: "",
});

export type TerminalHeaderProps = HTMLAttributes<HTMLDivElement>;

export const TerminalHeader = ({
  className,
  children,
  ...props
}: TerminalHeaderProps) => (
  <div
    className={cn(
      "flex items-center justify-between gap-2 border-white/10 border-b py-2.5 pr-3 pl-4",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

export type TerminalTitleProps = HTMLAttributes<HTMLDivElement>;

export const TerminalTitle = ({
  className,
  children,
  ...props
}: TerminalTitleProps) => (
  <div
    className={cn(
      "min-w-0 flex-1 truncate font-medium text-muted-foreground text-xs",
      className,
    )}
    {...props}
  >
    {children ?? "Terminal"}
  </div>
);

export type TerminalStatusProps = HTMLAttributes<HTMLDivElement>;

export const TerminalStatus = ({
  className,
  children,
  ...props
}: TerminalStatusProps) => {
  const { isStreaming } = useContext(TerminalContext);

  if (!isStreaming) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 font-normal text-muted-foreground text-xs",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export type TerminalActionsProps = HTMLAttributes<HTMLDivElement>;

export const TerminalActions = ({
  className,
  children,
  ...props
}: TerminalActionsProps) => (
  <div className={cn("flex items-center gap-1", className)} {...props}>
    {children}
  </div>
);

export type TerminalCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const TerminalCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: TerminalCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<number>(0);
  const { output } = useContext(TerminalContext);

  const copyToClipboard = useCallback(async () => {
    if (typeof window === "undefined" || !navigator?.clipboard?.writeText) {
      onError?.(new Error("Clipboard API not available"));
      return;
    }

    try {
      await navigator.clipboard.writeText(output);
      setIsCopied(true);
      onCopy?.();
      timeoutRef.current = window.setTimeout(() => setIsCopied(false), timeout);
    } catch (error) {
      onError?.(error as Error);
    }
  }, [output, onCopy, onError, timeout]);

  useEffect(
    () => () => {
      window.clearTimeout(timeoutRef.current);
    },
    [],
  );

  return (
    <Button
      aria-label={isCopied ? "Copied" : "Copy terminal output"}
      className={cn(
        "size-5 shrink-0 text-muted-foreground hover:bg-accent hover:text-foreground",
        className,
      )}
      onClick={copyToClipboard}
      size="icon"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon icon={isCopied ? Check : Copy} size={12} />}
    </Button>
  );
};

export type TerminalClearButtonProps = ComponentProps<typeof Button>;

export const TerminalClearButton = ({
  children,
  className,
  ...props
}: TerminalClearButtonProps) => {
  const { onClear } = useContext(TerminalContext);

  if (!onClear) {
    return null;
  }

  return (
    <Button
      aria-label="Clear terminal"
      className={cn(
        "size-5 shrink-0 text-muted-foreground hover:bg-accent hover:text-foreground",
        className,
      )}
      onClick={onClear}
      size="icon"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon icon={X} size={12} />}
    </Button>
  );
};

export type TerminalContentProps = HTMLAttributes<HTMLDivElement>;

export const TerminalContent = ({
  className,
  children,
  ...props
}: TerminalContentProps) => {
  const { output, isStreaming, autoScroll } = useContext(TerminalContext);
  const containerRef = useRef<HTMLDivElement>(null);

  // `output` is the trigger (not read in the effect body). Listing it is
  // intentional so the scroll-to-bottom fires every time new content
  // streams in — removing it stops autoscroll mid-stream.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [output, autoScroll]);

  return (
    <div
      className={cn(
        "max-h-96 overflow-auto px-4 pt-3 pb-3.5 font-mono font-normal text-foreground text-xs leading-[18px]",
        className,
      )}
      ref={containerRef}
      {...props}
    >
      {children ?? (
        <pre className="whitespace-pre-wrap break-words">
          <Ansi>{output}</Ansi>
          {isStreaming && (
            <span className="ml-0.5 inline-block h-3.5 w-[7px] animate-pulse bg-foreground align-middle" />
          )}
        </pre>
      )}
    </div>
  );
};

export type TerminalProps = HTMLAttributes<HTMLDivElement> & {
  output: string;
  isStreaming?: boolean;
  autoScroll?: boolean;
  onClear?: () => void;
};

export const Terminal = ({
  output,
  isStreaming = false,
  autoScroll = true,
  onClear,
  className,
  children,
  ...props
}: TerminalProps) => {
  const contextValue = useMemo(
    () => ({ autoScroll, isStreaming, onClear, output }),
    [autoScroll, isStreaming, onClear, output],
  );

  return (
    <TerminalContext.Provider value={contextValue}>
      <div
        className={cn(
          "dark flex flex-col overflow-hidden rounded-md border border-white/10 bg-background text-foreground",
          className,
        )}
        {...props}
      >
        {children ?? (
          <>
            <TerminalHeader>
              <TerminalTitle />
              <div className="flex items-center gap-1">
                <TerminalStatus />
                <TerminalActions>
                  <TerminalCopyButton />
                  {onClear && <TerminalClearButton />}
                </TerminalActions>
              </div>
            </TerminalHeader>
            <TerminalContent />
          </>
        )}
      </div>
    </TerminalContext.Provider>
  );
};
